import { motion } from "framer-motion";
import { Eye, Star, ThumbsUp, ArrowUpRight } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { formatNum } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=800";

const Metric = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-volt" />
      <span className="font-display font-bold text-base">{value}</span>
    </div>
    <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-muted-foreground mt-1">{label}</div>
  </div>
);

export const GameCard = ({ game, index }) => (
  <motion.a
    href={game.url || "#"} target="_blank" rel="noreferrer"
    data-testid={`game-card-${game.id}`}
    initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="group relative block border border-border bg-card overflow-hidden hover:border-volt transition-colors duration-300"
  >
    <div className="relative aspect-video overflow-hidden">
      <img src={game.thumbnail_url || FALLBACK} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
      <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 px-2.5 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" />
        <span className="font-mono text-[10px] tracking-[0.15em] uppercase" data-testid={`game-playing-${game.id}`}>{formatNum(game.playing)} playing</span>
      </div>
      <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center border border-white/20 bg-black/40 backdrop-blur-md group-hover:bg-volt group-hover:border-volt transition-colors duration-300">
        <ArrowUpRight className="w-4 h-4 group-hover:text-white transition-colors duration-300" />
      </div>
    </div>
    <div className="p-5">
      <h4 className="font-display font-bold text-lg tracking-tight uppercase truncate">{game.name || "Untitled Game"}</h4>
      {Array.isArray(game.history) && game.history.length >= 2 && (
        <div className="mt-3" data-testid={`game-trend-${game.id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-muted-foreground">Players over time</span>
            <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-volt">{formatNum(game.playing)} now</span>
          </div>
          <div className="h-10 -mx-1 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={game.history.map((h) => ({ v: h.playing }))} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
                <defs>
                  <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF2E2E" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#FF2E2E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke="#FF2E2E" strokeWidth={1.5} fill="url(#spark)" isAnimationActive={false} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <Metric icon={Eye} label="Visits" value={formatNum(game.visits)} />
        <Metric icon={Star} label="Favorites" value={formatNum(game.favorites)} />
        <Metric icon={ThumbsUp} label="Rating" value={game.like_ratio ? game.like_ratio + "%" : "—"} />
      </div>
    </div>
  </motion.a>
);
